import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onRegisterClick, redirectTo }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email.trim(), password, rememberMe);
      
      if (result.success) {
        // Clear form and close modal
        setEmail('');
        setPassword('');
        setRememberMe(false);
        onClose();
        
        // Redirect based on redirectTo prop or user role
        // Prioritize redirectTo if user was trying to access a specific page
        const userRole = result.user?.adminRole;
        if (redirectTo) {
          navigate(redirectTo);
        } else if (userRole === 'admin' || userRole === 'subadmin') {
          navigate('/admin/dashboard');
        }
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Handle Google login logic here
    console.log('Google login clicked');
  };

  // Use Portal to render modal at document body level
  return ReactDOM.createPortal(
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="login-modal-header">
          <h2 className="login-modal-title">Login</h2>
        </div>

        <div className="login-modal-content">
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-form-fields">
              <div className="input-group">
                <div className="input-field">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="input-group">
                <div className="input-field password-field">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.21249 2.70437C4.15761 2.6425 4.09099 2.59214 4.01649 2.55621C3.942 2.52028 3.86111 2.49949 3.77853 2.49504C3.69594 2.4906 3.61329 2.50259 3.53537 2.53033C3.45746 2.55806 3.38582 2.60098 3.32461 2.65661C3.2634 2.71223 3.21384 2.77945 3.17881 2.85437C3.14377 2.92929 3.12395 3.01042 3.1205 3.09305C3.11704 3.17569 3.13003 3.25818 3.15869 3.33576C3.18736 3.41334 3.23114 3.48446 3.28749 3.545L4.79062 5.19891C1.95312 6.94031 0.732805 9.62469 0.678899 9.74656C0.643362 9.82649 0.625 9.91299 0.625 10.0005C0.625 10.0879 0.643362 10.1744 0.678899 10.2544C0.706243 10.3161 1.36796 11.7833 2.83905 13.2544C4.79921 15.2137 7.27499 16.2497 9.99999 16.2497C11.4005 16.2577 12.7868 15.9693 14.068 15.4036L15.7867 17.295C15.8416 17.3569 15.9082 17.4072 15.9827 17.4432C16.0572 17.4791 16.1381 17.4999 16.2207 17.5043C16.3033 17.5088 16.3859 17.4968 16.4638 17.469C16.5417 17.4413 16.6134 17.3984 16.6746 17.3428C16.7358 17.2871 16.7854 17.2199 16.8204 17.145C16.8554 17.0701 16.8752 16.989 16.8787 16.9063C16.8822 16.8237 16.8692 16.7412 16.8405 16.6636C16.8118 16.586 16.7681 16.5149 16.7117 16.4544L4.21249 2.70437ZM7.91015 8.62937L11.1656 12.2114C10.6754 12.4693 10.1134 12.5571 9.56783 12.461C9.02229 12.3648 8.52413 12.0903 8.15157 11.6803C7.77901 11.2704 7.55314 10.7483 7.50945 10.1961C7.46575 9.64389 7.60669 9.0928 7.91015 8.62937ZM9.99999 14.9997C7.5953 14.9997 5.49452 14.1255 3.75546 12.402C3.04164 11.6926 2.43454 10.8834 1.95312 9.99969C2.31952 9.31297 3.48905 7.39109 5.65233 6.14187L7.05858 7.68484C6.51416 8.38211 6.23377 9.2494 6.26697 10.1334C6.30017 11.0174 6.64483 11.8612 7.24002 12.5157C7.83521 13.1702 8.64261 13.5932 9.51951 13.7099C10.3964 13.8266 11.2864 13.6296 12.032 13.1536L13.1828 14.4192C12.1672 14.8089 11.0878 15.0057 9.99999 14.9997ZM10.4687 7.54422C10.3059 7.51314 10.162 7.41863 10.0689 7.2815C9.97567 7.14436 9.94079 6.97583 9.97187 6.81297C10.0029 6.65011 10.0974 6.50627 10.2346 6.41308C10.3717 6.3199 10.5403 6.28501 10.7031 6.31609C11.4997 6.47052 12.225 6.87832 12.7708 7.47866C13.3167 8.079 13.6539 8.83972 13.732 9.64734C13.7475 9.81238 13.6967 9.97678 13.5909 10.1044C13.4851 10.232 13.333 10.3124 13.168 10.3278C13.1484 10.329 13.1289 10.329 13.1094 10.3278C12.9532 10.3285 12.8024 10.2706 12.6867 10.1656C12.571 10.0607 12.4988 9.91617 12.4844 9.76062C12.4318 9.22343 12.2072 8.71756 11.844 8.31823C11.4809 7.91891 10.9985 7.64745 10.4687 7.54422ZM19.3187 10.2544C19.2859 10.3278 18.4945 12.0802 16.7125 13.6762C16.6516 13.7325 16.5801 13.7761 16.5022 13.8045C16.4243 13.8329 16.3415 13.8455 16.2587 13.8416C16.1759 13.8376 16.0947 13.8173 16.0198 13.7817C15.9449 13.746 15.8779 13.6959 15.8226 13.6341C15.7673 13.5723 15.7249 13.5002 15.6977 13.4218C15.6706 13.3435 15.6593 13.2605 15.6645 13.1778C15.6698 13.095 15.6914 13.0141 15.7282 12.9398C15.765 12.8655 15.8162 12.7993 15.8789 12.745C16.7532 11.9595 17.4876 11.0312 18.0508 9.99969C17.5683 9.11514 16.9598 8.30538 16.2445 7.59578C14.5055 5.87391 12.4047 4.99969 9.99999 4.99969C9.49332 4.99907 8.98745 5.04009 8.48749 5.12234C8.40619 5.13672 8.32284 5.13485 8.24227 5.11683C8.1617 5.09881 8.08549 5.06501 8.01805 5.01738C7.95062 4.96974 7.89329 4.90922 7.84938 4.8393C7.80547 4.76938 7.77584 4.69145 7.76222 4.61002C7.74859 4.52859 7.75124 4.44527 7.77 4.36486C7.78877 4.28446 7.82327 4.20857 7.87153 4.14158C7.91979 4.07459 7.98085 4.01782 8.05117 3.97456C8.12149 3.9313 8.19969 3.9024 8.28124 3.88953C8.84934 3.79573 9.4242 3.74896 9.99999 3.74969C12.725 3.74969 15.2008 4.78562 17.1609 6.74578C18.632 8.21687 19.2937 9.68484 19.3211 9.74656C19.3566 9.82649 19.375 9.91299 19.375 10.0005C19.375 10.0879 19.3566 10.1744 19.3211 10.2544H19.3187Z"
                        fill="#6D6D6D"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="login-options">
                <div className="remember-me">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <svg
                      className="checkbox-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15 3.75H5C4.30964 3.75 3.75 4.30964 3.75 5V15C3.75 15.6903 4.30964 16.25 5 16.25H15C15.6903 16.25 16.25 15.6903 16.25 15V5C16.25 4.30964 15.6903 3.75 15 3.75ZM5 2.5C3.61929 2.5 2.5 3.61929 2.5 5V15C2.5 16.3807 3.61929 17.5 5 17.5H15C16.3807 17.5 17.5 16.3807 17.5 15V5C17.5 3.61929 16.3807 2.5 15 2.5H5Z"
                        fill="#6D6D6D"
                      />
                    </svg>
                    <span className="remember-text">remember</span>
                  </label>
                </div>
                <div className="forgot-password">
                  <span>Forgot Password?</span>
                </div>
              </div>

              {error && (
                <div className="login-error-message" style={{ color: '#e74c3c', fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
                  {error}
                </div>
              )}
            </div>

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Continue'}
            </button>
          </form>
        </div>

        <div className="login-footer">
          <p className="register-link">
            Don't have an account? <span className="register-text" onClick={onRegisterClick} style={{ cursor: 'pointer' }}>Register</span>
          </p>

          <div className="divider">
            <div className="divider-line"></div>
            <span className="divider-text">Or</span>
            <div className="divider-line"></div>
          </div>
        </div>

        <button className="google-login-button" onClick={handleGoogleLogin}>
          <div className="google-button-content">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.8 10.208C18.8 9.55801 18.7417 8.93301 18.6333 8.33301H10V11.883H14.9333C14.7167 13.0247 14.0667 13.9913 13.0917 14.6413V16.9497H16.0667C17.8 15.3497 18.8 12.9997 18.8 10.208Z"
                fill="#4285F4"
              />
              <path
                d="M9.99974 19.1667C12.4747 19.1667 14.5497 18.35 16.0664 16.95L13.0914 14.6417C12.2747 15.1917 11.2331 15.525 9.99974 15.525C7.61641 15.525 5.59141 13.9167 4.86641 11.75H1.81641V14.1167C3.32474 17.1083 6.41641 19.1667 9.99974 19.1667Z"
                fill="#07783F"
              />
              <path
                d="M4.86732 11.7416C4.68398 11.1916 4.57565 10.6083 4.57565 9.99993C4.57565 9.3916 4.68398 8.80827 4.86732 8.25827V5.8916H1.81732C1.19232 7.12493 0.833984 8.5166 0.833984 9.99993C0.833984 11.4833 1.19232 12.8749 1.81732 14.1083L4.19232 12.2583L4.86732 11.7416Z"
                fill="#FBBC05"
              />
              <path
                d="M9.99974 4.48301C11.3497 4.48301 12.5497 4.94968 13.5081 5.84968L16.1331 3.22467C14.5414 1.74134 12.4747 0.833008 9.99974 0.833008C6.41641 0.833008 3.32474 2.89134 1.81641 5.89134L4.86641 8.25801C5.59141 6.09134 7.61641 4.48301 9.99974 4.48301Z"
                fill="#EA4335"
              />
            </svg>
            <span>Continue with Google</span>
          </div>
        </button>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
